/* eslint-disable no-param-reassign */
import React from 'react';
import {
  eachDayOfInterval,
  format,
  getYear,
  isBefore,
  isSameDay,
  parseISO,
} from 'date-fns';
import type { TFunction } from 'i18next';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import useMonthlyPlanningParams from 'containers/ResourcePlanning/MonthlyView/useMonthlyPlanningParams';
import { getMonthlyPlanningDates } from 'containers/ResourcePlanning/MonthlyView/utils';
import { projectAtom } from 'containers/ResourcePlanning/Scheduler/atoms';
import showPlanningToast from 'containers/ResourcePlanning/showPlanningToast';
import useHandlePlanningError, {
  showPlanningErrorToast,
} from 'containers/ResourcePlanning/useHandlePlanningError';
import type { ProjectTask } from 'model/Projects/types';
import type { MutatorCallback, MutatorOptions } from 'swr';
import useSWR, { mutate } from 'swr';

import { useSchedulerParams } from 'containers/ResourcePlanning/Scheduler/useSchedulerParams';
import useCurrentUserAndSettings from 'hooks/useCurrentUserAndSettings';
import { checkStatus, del, get, post, put } from 'libs/apiClient';
import { localeFormat } from 'libs/dateFunctions';
import type { CompanyTag } from 'model/CompanyTags/types';
import type {
  AssignedResources,
  CompactUserPlans,
  FiltersForPlanning,
  InternalNote,
  MachineForPlanning,
  MachinePlan,
  NewInternalNote,
  NewMachineTask,
  NewPlanningSettings,
  NewTask,
  PlannerResource,
  PlanningSettings,
  RepeatedMachinePlanPayload,
  RepeatedPlanPayload,
  Role,
  UserForPlanning,
  UserPlan,
  UserSortType,
} from 'model/ResourcePlanning/types';
import {
  getFirstAndLastDayOfWeek,
  getPlannerDateString,
  getQueryString,
  getWeekForPlanning,
  plannerDateFormat,
  planPlaceholderBase,
} from 'model/ResourcePlanning/utils';

import { useApi, usePostSWRFetcher } from './base-api';

const baseUrl = '/api/project-plan';
const machineBaseUrl = '/api/machine-project-plan';
const USER_ID_TO_IGNORE = 'USRM_MainProjectNode';
export const internalCommentBaseUrl = '/api/planning-comments';

type PlanningSettingsResponse = Omit<
  ReturnType<typeof useApi<PlanningSettings | null>>,
  'data'
> & {
  data: PlanningSettings | NewPlanningSettings;
  rawData: PlanningSettings | null | undefined;
};

export const useGetPlanningSettings = (
  revalidateOnMount = false
): PlanningSettingsResponse => {
  const { currentUser } = useCurrentUserAndSettings();

  const { data, ...rest } = useApi<PlanningSettings | null>(
    `${baseUrl}/settings/${currentUser.id}`,
    {
      revalidateOnMount,
    }
  );

  return {
    data: data || {
      userId: currentUser.id,
      settings: {
        showTaskNumber: false,
        showRoles: false,
        showResources: false,
        showComments: false,
        showEmployeeNumbers: false,
        onlyShowUsersLackingPlans: false,
      },
    },
    rawData: data,
    ...rest,
  };
};

export const useSavePlanningSettings = () => {
  const { currentUser } = useCurrentUserAndSettings();
  const handleError = useHandlePlanningError();

  return async (settings: NewPlanningSettings['settings']) => {
    try {
      mutate<PlanningSettings | null>(
        `${baseUrl}/settings/${currentUser.id}`,
        async (data) => {
          const method = data ? put : post;

          const newRecentPlans = settings.recentPlans
            ? // Filtering out plans that are already in the recentPlans array to put them at the top of the list
              data?.settings?.recentPlans?.filter(
                (plan) =>
                  !settings.recentPlans!.some(
                    (recentPlan) =>
                      recentPlan.projectId === plan.projectId &&
                      recentPlan.task.taskId === plan.task.taskId
                  )
              ) || []
            : data?.settings?.recentPlans || [];
          if (settings.recentPlans) {
            newRecentPlans.unshift(...settings.recentPlans);
          }

          const response = await checkStatus(
            await method(
              `${baseUrl}/settings`,
              data
                ? {
                    ...data,
                    settings: {
                      ...data.settings,
                      ...settings,
                      recentPlans: newRecentPlans.slice(0, 10),
                    },
                  }
                : {
                    userId: currentUser.userId,
                    settings,
                  }
            )
          );
          const updatedSettings: PlanningSettings = await response.json();

          return updatedSettings;
        },
        {
          revalidate: false,
          optimisticData: (data) => {
            const newRecentPlans = settings.recentPlans
              ? // Filtering out plans that are already in the recentPlans array to put them at the top of the list
                data?.settings?.recentPlans?.filter(
                  (plan) =>
                    !settings.recentPlans!.some(
                      (recentPlan) =>
                        recentPlan.projectId === plan.projectId &&
                        recentPlan.task.taskId === plan.task.taskId
                    )
                ) || []
              : data?.settings?.recentPlans || [];
            if (settings.recentPlans) {
              newRecentPlans.unshift(...settings.recentPlans);
            }

            return data
              ? {
                  ...data,
                  settings: {
                    ...data.settings,
                    ...settings,
                    recentPlans: newRecentPlans.slice(0, 10),
                  },
                }
              : {
                  id: 'optimistic-data',
                  userId: currentUser.id,
                  companyId: currentUser.companyId,
                  collectionRef: 'PlannerUserSettingsDS',
                  settings,
                };
          },
        }
      );
    } catch (e) {
      handleError(e);
    }
  };
};

export const useRemoveInactiveTaskFromPlanningSettings = () => {
  const { currentUser } = useCurrentUserAndSettings();
  const handleError = useHandlePlanningError();

  return async (taskId: string) => {
    try {
      mutate<PlanningSettings | null>(
        `${baseUrl}/settings/${currentUser.id}`,
        async (data) => {
          if (!data?.settings?.recentPlans) {
            return data;
          }

          const response = await checkStatus(
            await put(`${baseUrl}/settings`, {
              ...data,
              settings: {
                ...data.settings,
                recentPlans: data.settings.recentPlans.filter(
                  (plan) => plan.task.taskId !== taskId
                ),
              },
            })
          );
          const updatedSettings: PlanningSettings = await response.json();

          return updatedSettings;
        },
        {
          revalidate: false,
          optimisticData: (data) => {
            if (!data?.settings?.recentPlans) {
              return data || null;
            }

            return {
              ...data,
              settings: {
                ...data.settings,
                recentPlans: data.settings.recentPlans.filter(
                  (plan) => plan.task.taskId !== taskId
                ),
              },
            };
          },
        }
      );
    } catch (e) {
      handleError(e);
    }
  };
};

interface MutatePlanArgs {
  projectId: string;
  week: number;
  year: number;
}

interface MutateUserPlanArgs extends MutatePlanArgs {
  userId: string;
  data?: UserForPlanning[] | MutatorCallback<UserForPlanning[]>;
  revalidation?: boolean | MutatorOptions<UserForPlanning[], UserForPlanning[]>;
}

interface MutateMachinePlanArgs extends MutatePlanArgs {
  machineId: string;
  /** Passing `null` means that cache will be invalidated */
  data?: MachineForPlanning[] | MutatorCallback<MachineForPlanning[]> | null;
  revalidation?:
    | boolean
    | MutatorOptions<MachineForPlanning[], MachineForPlanning[]>;
}

// Exported to be used in monthlyResourcePlanning.ts
export const mutateUserPlan = ({
  userId,
  projectId,
  week,
  year,
  data,
  revalidation = false,
}: MutateUserPlanArgs) => {
  const query = getQueryString(week, year);

  return mutate(
    `${baseUrl}/project/${projectId}/users/${userId}/?${query}`,
    data,
    revalidation
  );
};

// Exported to be used in monthlyResourcePlanning.ts
export const mutateMachinePlan = ({
  machineId,
  projectId,
  week,
  year,
  data,
  revalidation = false,
}: MutateMachinePlanArgs) => {
  const query = getQueryString(week, year);

  return mutate(
    `${machineBaseUrl}/project/${projectId}/machines/${machineId}/?${query}`,
    data === null ? () => undefined : data,
    revalidation
  );
};

export const revalidateMachinePlan = ({
  machineId,
  projectId,
  week,
  year,
}: MutateMachinePlanArgs) => {
  const query = getQueryString(week, year);

  return mutate(
    `${machineBaseUrl}/project/${projectId}/machines/${machineId}/?${query}`
  );
};

export const useUsersByProjectId = (
  projectId: string,
  revalidateOnMount = false
) => {
  const { data, error, ...rest } = useSWR<string[]>(
    projectId ? `${baseUrl}/users-planned-in-project/${projectId}` : null,
    { revalidateOnFocus: false, revalidateOnMount }
  );

  const userIdsForProject = React.useMemo(
    () => (data ? data.filter((id) => id !== USER_ID_TO_IGNORE) : []),
    [data]
  );

  const userIdsObject = React.useMemo(
    () => userIdsForProject.reduce((acc, cv) => ({ ...acc, [cv]: true }), {}),
    [userIdsForProject]
  );

  return {
    rawData: data,
    error,
    userIdsForProject,
    userIdsObject,
    isFetchingUsers: !data && !error,
    ...rest,
  };
};

export const useMachinesByProjectId = (
  projectId: string,
  revalidateOnMount = false
) => {
  const { data, error, ...rest } = useSWR<string[]>(
    projectId
      ? `${machineBaseUrl}/machines-planned-in-project/${projectId}`
      : null,
    { revalidateOnFocus: false, revalidateOnMount }
  );

  const machineIdsForProject = React.useMemo(
    () => (data ? data.filter((id) => id !== USER_ID_TO_IGNORE) : []),
    [data]
  );

  const machineIdsObject = React.useMemo(
    () =>
      machineIdsForProject.reduce((acc, cv) => ({ ...acc, [cv]: true }), {}),
    [machineIdsForProject]
  );

  return {
    error,
    machineIdsForProject,
    machineIdsObject,
    isFetchingMachines: !data && !error,
    ...rest,
  };
};

// Exported to be used in monthlyResourcePlanning.ts
export const isExistingPlan = (
  plan:
    | UserPlan
    | Omit<
        RepeatedPlanPayload,
        'daysOfWeek' | 'dates' | 'startDate' | 'endDate'
      >
    | NewTask
): plan is UserPlan => !!(plan as UserPlan).id;

// Exported to be used in monthlyResourcePlanning.ts
export interface UserPlanMutationArgs {
  t: TFunction;
  skipDuplicateCheck?: boolean;
  newPlans: UserPlan[];
  payload:
    | Omit<
        RepeatedPlanPayload,
        'daysOfWeek' | 'dates' | 'startDate' | 'endDate'
      >
    | NewTask
    | UserPlan;
  projectId: string;
  isBeforeApiCall?: boolean;
  originalDate?: string;
  originalId?: string | null;
  originalUserId?: string;
}

// Exported to be used in monthlyResourcePlanning.ts
export const removePlanFromOriginalDate = async (
  userId: string,
  projectId: string,
  oldDate: Date,
  oldDateString: string,
  payload: UserPlan
) => {
  await mutateUserPlan({
    userId,
    projectId,
    week: getWeekForPlanning(oldDate),
    year: getYear(oldDate),
    data: (data) => {
      // Removing plan that was moved to a different date
      const newData = data && structuredClone(data);
      if (newData) {
        const dateKey = getPlannerDateString(oldDateString);
        newData[0].plans[dateKey].projectPlans = newData[0].plans[
          dateKey
        ].projectPlans.filter((plan) => plan.id !== payload.id);
      }

      return newData;
    },
    revalidation: false,
  });
};

// Exported to be used in monthlyResourcePlanning.ts
export const mutateUserAndMachinePlans = async ({
  t,
  skipDuplicateCheck,
  newPlans,
  payload,
  projectId,
  isBeforeApiCall,
  originalDate,
  originalId,
  originalUserId,
}: UserPlanMutationArgs) => {
  if (!newPlans.length) {
    return {};
  }

  const { userId } = payload;

  const oldDate = originalDate && parseISO(originalDate);
  const newDate = parseISO(newPlans[0].date);

  const plansByWeekAndYear = newPlans.reduce(
    (pv, cv) => {
      const date = parseISO(cv.date);
      const key = `${getYear(date)}-${getWeekForPlanning(date)}`;
      if (pv[key]) {
        pv[key].push(cv);
      } else {
        pv[key] = [cv];
      }

      return pv;
    },
    {} as { [weekAndYear: string]: UserPlan[] }
  );

  // We are creating the same plan on each day so we can just pull machineIds from the first plan
  const { machineIds } = newPlans[0];

  // Filtering out dates where task is already planned (relevant for planning on multiple dates)
  const relevantDates: string[] = [];
  const irrelevantDates: string[] = [];

  await Promise.all(
    Object.entries(plansByWeekAndYear).map(async ([weekAndYear, plans]) => {
      const [year, week] = weekAndYear.split('-');

      await mutateUserPlan({
        userId,
        projectId,
        week: Number(week),
        year: Number(year),
        data: (data) => {
          const newData = data && structuredClone(data);
          if (newData) {
            plans.forEach((plan) => {
              const dateKey = plan.date.replace('Z', '');
              if (plan.id) {
                // Filtering out placeholders when actual plans arrived
                newData[0].plans[dateKey].projectPlans = newData[0].plans[
                  dateKey
                ].projectPlans.filter((projectPlan) => projectPlan.id);
                // Adding a new plan
                newData[0].plans[dateKey].projectPlans.push(plan);
              } else if (
                !skipDuplicateCheck &&
                newData[0].plans[dateKey].projectPlans.some(
                  (existingPlan) => existingPlan.taskId === plan.taskId
                )
              ) {
                if (newPlans.length === 1) {
                  throw new Error(
                    t('ResourcePlanning.NameAlreadyHasTaskPlanned', {
                      name: newData[0].userName,
                      taskName: plan.taskName,
                      date: localeFormat(parseISO(plan.date)),
                    })
                  );
                } else {
                  irrelevantDates.push(plan.date);
                }
              } else {
                relevantDates.push(plan.date);

                if (!originalUserId && oldDate && isSameDay(oldDate, newDate)) {
                  // Updating a plan if it wasn't moved to a different date
                  newData[0].plans[dateKey].projectPlans = newData[0].plans[
                    dateKey
                  ].projectPlans.map((projectPlan) =>
                    projectPlan.id === originalId ? newPlans[0] : projectPlan
                  );
                } else {
                  // Adding a new plan
                  newData[0].plans[dateKey].projectPlans.push(plan);
                }
              }
            });
          } else {
            // If newData is missing it means that plans for that week were not fetched yet
            // so it is up to backend to decide if new plan should be created or not
            plans.forEach((plan) => {
              relevantDates.push(plan.date);
            });
          }

          return newData;
        },
        revalidation: false,
      });

      // This should only happen when actual data arrived
      if (newPlans[0].id) {
        // Invalidating and revaldating cached plans of machines that were attached to new plans
        machineIds.forEach((machineId) => {
          mutateMachinePlan({
            machineId,
            projectId,
            week: Number(week),
            year: Number(year),
            data: null,
            revalidation: true,
          });
        });
      }
    })
  );

  if (isBeforeApiCall && oldDate && isExistingPlan(payload)) {
    // If original date is provided and payload is an existing plan
    if (!isSameDay(oldDate, newDate)) {
      // If plan's date was changed
      await removePlanFromOriginalDate(
        // If originalUserId is present then the plan was moved from one user to another
        // and we need to remove this plan for the original user
        originalUserId || userId,
        projectId,
        oldDate,
        originalDate,
        payload
      );
    } else if (originalUserId && userId !== originalUserId) {
      // If date wasn't changed but plan was moved to a different user
      await mutateUserPlan({
        userId: originalUserId,
        projectId,
        week: getWeekForPlanning(oldDate),
        year: getYear(oldDate),
        data: (data) => {
          // Removing plan that was moved to a different date
          const newData = data && structuredClone(data);
          if (newData) {
            const dateKey = getPlannerDateString(originalDate);
            newData[0].plans[dateKey].projectPlans = newData[0].plans[
              dateKey
            ].projectPlans.filter((plan) => plan.id !== payload.id);
          }

          return newData;
        },
        revalidation: false,
      });
    }
  }

  return { relevantDates, irrelevantDates };
};

export interface PlanWithOriginalDate {
  plan: UserPlan;
  originalDate: string;
}

// Exported to be used in monthlyResourcePlanning.ts
export const isRegularPlansArray = (
  plan: UserPlan[] | PlanWithOriginalDate[]
): plan is UserPlan[] => !!(plan as UserPlan[])[0].id;

// Exported to be used in monthlyResourcePlanning.ts
export interface MultiPlanMutationArgs {
  newPlans: UserPlan[] | PlanWithOriginalDate[];
  creatingPlaceholders: boolean;
  projectId: string;
}

// Exported to be used in monthlyResourcePlanning.ts
export const removeMultipleUserPlans = ({
  newPlans,
  projectId,
}: Omit<MultiPlanMutationArgs, 'creatingPlaceholders'>) => {
  if (!isRegularPlansArray(newPlans)) {
    // If plans were moved to new dates (they are plans with original dates) we need to remove plans from original dates
    const originalDate = parseISO(newPlans[0].originalDate);
    const year = getYear(originalDate);
    const week = getWeekForPlanning(originalDate);

    const plansByUserId = newPlans.reduce(
      (pv, cv) => {
        if (pv[cv.plan.userId]) {
          pv[cv.plan.userId].push({
            ...cv.plan,
            date: cv.originalDate,
          });
        } else {
          pv[cv.plan.userId] = [
            {
              ...cv.plan,
              date: cv.originalDate,
            },
          ];
        }

        return pv;
      },
      {} as { [userId: string]: UserPlan[] }
    );

    Object.entries(plansByUserId).forEach(async ([userId, plans]) => {
      mutateUserPlan({
        userId,
        projectId,
        week: Number(week),
        year: Number(year),
        data: (data) => {
          const newData = data && structuredClone(data);
          if (newData) {
            plans.forEach((newPlan) => {
              const dateKey = newPlan.date.replace('Z', '');
              newData[0].plans[dateKey].projectPlans = newData[0].plans[
                dateKey
              ].projectPlans.filter((plan) => plan.taskId !== newPlan.taskId);
            });
          }

          return newData;
        },
        revalidation: false,
      });
    });
  }
};

// Exported to be used in monthlyResourcePlanning.ts
export const mutateMultipleUserPlans = ({
  newPlans,
  creatingPlaceholders,
  projectId,
}: MultiPlanMutationArgs) => {
  if (!newPlans.length) {
    return;
  }

  if (creatingPlaceholders) {
    removeMultipleUserPlans({ newPlans, projectId });
  }

  const updatedPlans = isRegularPlansArray(newPlans)
    ? newPlans
    : newPlans.map(({ plan }) => plan);

  const year = getYear(parseISO(updatedPlans[0].date));
  const week = getWeekForPlanning(parseISO(updatedPlans[0].date));

  const plansByUserId = updatedPlans.reduce(
    (pv, cv) => {
      if (pv[cv.userId]) {
        pv[cv.userId].push(cv);
      } else {
        pv[cv.userId] = [cv];
      }

      return pv;
    },
    {} as { [userId: string]: UserPlan[] }
  );

  Object.entries(plansByUserId).map(async ([userId, plans]) => {
    mutateUserPlan({
      userId,
      projectId,
      week: Number(week),
      year: Number(year),
      data: (data) => {
        const newData = data && structuredClone(data);
        if (newData) {
          plans.forEach((newPlan) => {
            const dateKey = newPlan.date.replace('Z', '');
            if (creatingPlaceholders) {
              if (
                newData[0].plans[dateKey].projectPlans.find(
                  (plan) => plan.id === newPlan.id
                )
              ) {
                newData[0].plans[dateKey].projectPlans = newData[0].plans[
                  dateKey
                ].projectPlans.map((plan) =>
                  plan.id === newPlan.id
                    ? {
                        ...newPlan,
                        id: null,
                      }
                    : plan
                );
              } else {
                /* If plan with matching ID is missing on a day
                then it means that new plans are being created
                (e.g. plans were moved or copied) */
                newData[0].plans[dateKey].projectPlans.push({
                  ...newPlan,
                  id: null,
                  refId: null,
                });
              }
            } else {
              newData[0].plans[dateKey].projectPlans = newData[0].plans[
                dateKey
                // Filtering out placeholder plans
              ].projectPlans.filter((plan) => plan.id);
              newData[0].plans[dateKey].projectPlans.push(newPlan);
            }
          });
        }

        return newData;
      },
      revalidation: false,
    });

    // This should only happen when actual data arrived
    if (!creatingPlaceholders) {
      // We are applying the same updates to all plans so we can just pull machineIds from the first plan
      const { machineIds } = plans[0];
      // Invalidating and revaldating cached plans of machines that were attached to new plans
      machineIds.forEach((machineId) => {
        mutateMachinePlan({
          machineId,
          projectId,
          week: Number(week),
          year: Number(year),
          data: null,
          revalidation: true,
        });
      });
    }
  });
};

// Exported to be used in monthlyResourcePlanning.ts
export interface CreateOrUpdatePlanArgs {
  payload: NewTask | UserPlan;
  projectId: string;
  skipDuplicateCheck?: boolean;
  originalDate?: string;
  originalUserId?: string;
}

export const useCreateOrUpdatePlan = () => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();
  const savePlanningSettings = useSavePlanningSettings();

  return async ({
    payload,
    skipDuplicateCheck,
    projectId,
    originalDate,
    originalUserId,
  }: CreateOrUpdatePlanArgs) => {
    try {
      // Adding placeholder plan
      await mutateUserAndMachinePlans({
        t,
        skipDuplicateCheck,
        newPlans: [
          { ...payload, id: null, refId: null } as unknown as UserPlan,
        ],
        payload,
        projectId,
        isBeforeApiCall: true,
        originalDate,
        originalId: payload.id,
        originalUserId,
      });
      const response = checkStatus(
        await post(`${baseUrl}/create-or-update`, [payload])
      );
      if (payload.id) {
        if (
          (originalDate &&
            !isSameDay(parseISO(originalDate), parseISO(payload.date))) ||
          originalUserId
        ) {
          showPlanningToast(t('ResourcePlanning.Toasts.Moved', { count: 1 }));
        } else {
          showPlanningToast(t('ResourcePlanning.Toasts.Updated', { count: 1 }));
        }
      } else {
        showPlanningToast(t('ResourcePlanning.Toasts.Created'));
      }
      const newPlans: UserPlan[] = await response.json();
      mutateUserAndMachinePlans({
        t,
        newPlans,
        payload,
        projectId,
        originalDate,
      });

      if (!payload.id) {
        savePlanningSettings({
          recentPlans: [
            {
              id: newPlans[0].id!,
              projectId: payload.projectId,
              projectName: newPlans[0].projectName,
              task: {
                taskId: newPlans[0].taskId,
                taskName: newPlans[0].taskName,
                taskNumber: newPlans[0].taskNumber,
                taskNumberAndName: `${newPlans[0].taskNumber} - ${newPlans[0].taskName}`,
                parentNodeId: null,
                isSelected: false,
                isFolder: false,
                childNodes: [],
              },
            },
          ],
        });
      }
    } catch (e) {
      mutateUserPlan({
        userId: payload.userId,
        projectId,
        week: getWeekForPlanning(parseISO(payload.date)),
        year: parseISO(payload.date).getFullYear(),
        revalidation: true,
      });
      handleError(e);
    }
  };
};

export const useCreateMultiplePlans = () => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();

  return async (payload: UserPlan[], projectId: string) => {
    try {
      mutateMultipleUserPlans({
        newPlans: payload.map((plan) => ({
          plan: { ...plan, id: null },
          originalDate: plan.date,
        })),
        creatingPlaceholders: true,
        projectId,
      });
      const response = checkStatus(
        await post(
          `${baseUrl}/create-or-update`,
          payload.map((plan) => ({
            ...plan,
            id: null,
          }))
        )
      );
      const newPlans: UserPlan[] = await response.json();
      showPlanningToast(
        t('ResourcePlanning.Toasts.Created', { count: newPlans.length })
      );
      mutateMultipleUserPlans({
        newPlans,
        creatingPlaceholders: false,
        projectId,
      });
    } catch (e) {
      mutateUserPlan({
        userId: payload[0].userId,
        projectId,
        week: getWeekForPlanning(parseISO(payload[0].date)),
        year: parseISO(payload[0].date).getFullYear(),
        revalidation: true,
      });
      handleError(e);
    }
  };
};

// Exported to be used in monthlyResourcePlanning.ts
export interface UpdateMultiplePlansArgs {
  payload: UserPlan[] | PlanWithOriginalDate[];
  projectId: string;
}

export const useUpdateMultiplePlans = () => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();

  return async ({ payload, projectId }: UpdateMultiplePlansArgs) => {
    try {
      mutateMultipleUserPlans({
        newPlans: payload,
        creatingPlaceholders: true,
        projectId,
      });
      const response = checkStatus(
        await post(
          `${baseUrl}/create-or-update`,
          isRegularPlansArray(payload)
            ? payload
            : payload.map(({ plan }) => plan)
        )
      );
      const newPlans: UserPlan[] = await response.json();
      showPlanningToast(
        t('ResourcePlanning.Toasts.Updated', { count: newPlans.length })
      );
      mutateMultipleUserPlans({
        newPlans,
        creatingPlaceholders: false,
        projectId,
      });
    } catch (e) {
      const weeksAndYearsByUserId: { [userId: string]: Set<string> } = {};
      const updateWeeksAndYears = (plan: UserPlan) => {
        const date = parseISO(plan.date);

        if (weeksAndYearsByUserId[plan.userId]) {
          weeksAndYearsByUserId[plan.userId].add(
            `${getWeekForPlanning(date)}-${date.getFullYear()}`
          );
        } else {
          weeksAndYearsByUserId[plan.userId] = new Set([
            `${getWeekForPlanning(date)}-${date.getFullYear()}`,
          ]);
        }
      };

      if (isRegularPlansArray(payload)) {
        payload.forEach((plan) => updateWeeksAndYears(plan));
      } else {
        payload.forEach(({ plan }) => updateWeeksAndYears(plan));
      }

      Object.entries(weeksAndYearsByUserId).forEach(
        ([userId, weeksAndYears]) => {
          Array.from(weeksAndYears).forEach((weekAndYear) => {
            const [week, year] = weekAndYear.split('-');

            mutateUserPlan({
              userId,
              projectId,
              week: Number(week),
              year: Number(year),
              revalidation: true,
            });
          });
        }
      );
      handleError(e);
    }
  };
};

export const useCreatePlanOnDates = () => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUserAndSettings();
  const handleError = useHandlePlanningError();

  return async (
    payload: Omit<RepeatedPlanPayload, 'daysOfWeek'>,
    projectId: string
  ) => {
    try {
      // Creating placeholders
      const { relevantDates, irrelevantDates } =
        await mutateUserAndMachinePlans({
          t,
          newPlans: payload.dates.map((date) => ({
            ...planPlaceholderBase,
            userId: payload.userId,
            machineIds: payload.machineIds,
            roles: payload.roles,
            isUserOnLoan: false,
            date,
            companyId: payload.companyId,
            projectId: payload.projectId,
            taskId: payload.taskId,
            comment: payload.comment,
            // Placeholder data
            companyIds: [payload.companyId],
            createdByUserId: currentUser.id,
            modifiedByUserId: currentUser.id,
            refId: null,
            taskName: payload.taskName,
            taskNumber: payload.taskNumber,
            moveReason: payload.moveReason,
            moveCategory: payload.moveCategory,
            moveCause: payload.moveCause,
          })),
          payload,
          projectId,
          isBeforeApiCall: true,
        });

      if (irrelevantDates?.length) {
        showPlanningErrorToast(
          t('ResourcePlanning.NameAlreadyHasTaskPlanned', {
            name: payload.userName,
            taskName: payload.taskName,
            date: irrelevantDates
              .map((date) => localeFormat(parseISO(date)))
              .join(', '),
          })
        );
      }

      const response = checkStatus(
        await post(`${baseUrl}/create-multiple-days`, {
          ...payload,
          createOnHoliday: true,
          dates: relevantDates,
        })
      );
      const newPlans: UserPlan[] = await response.json();
      showPlanningToast(
        t('ResourcePlanning.Toasts.Created', { count: newPlans.length })
      );
      mutateUserAndMachinePlans({ t, newPlans, payload, projectId });
    } catch (e) {
      const weeksByYear: { [year: number]: Set<number> } = {};
      payload.dates.forEach((date) => {
        const year = parseISO(date).getFullYear();
        if (weeksByYear[year]) {
          weeksByYear[year].add(getWeekForPlanning(parseISO(date)));
        } else {
          weeksByYear[year] = new Set([getWeekForPlanning(parseISO(date))]);
        }
      });
      Object.entries(weeksByYear).forEach(([year, weeks]) => {
        Array.from(weeks).forEach((week) => {
          mutateUserPlan({
            userId: payload.userId,
            projectId,
            week,
            year: Number(year),
            revalidation: true,
          });
        });
      });

      handleError(e);
    }
  };
};

// Exported to be used in monthlyResourcePlanning.ts
export type NewRepeatedPlanPayload = Omit<RepeatedPlanPayload, 'dates'> & {
  createOnHoliday: boolean;
};
// Exported to be used in monthlyResourcePlanning.ts
export type NewRepeatedMachinePlanPayload = Omit<
  RepeatedMachinePlanPayload,
  'dates'
> & {
  createOnHoliday: boolean;
};

// Exported to be used in monthlyResourcePlanning.ts
export const isUserPlanPayload = (
  payload: NewRepeatedPlanPayload | NewRepeatedMachinePlanPayload
): payload is NewRepeatedPlanPayload =>
  !!(payload as NewRepeatedPlanPayload).userId;

const createRepeatedPlaceholders = (
  t: TFunction,
  payload: NewRepeatedPlanPayload | NewRepeatedMachinePlanPayload,
  year: number,
  week: number,
  currentUserId: string
) => {
  const startDate = parseISO(payload.startDate);
  const endDate = parseISO(payload.endDate);

  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  const startWeek = getWeekForPlanning(startDate);
  const endWeek = getWeekForPlanning(endDate);

  const irrelevantDates: string[] = [];

  if (
    startYear <= year &&
    endYear >= year &&
    startWeek <= week &&
    endWeek >= week
  ) {
    const { firstDayOfWeek: weekStart, lastDayOfWeek: weekEnd } =
      getFirstAndLastDayOfWeek(week, year);
    const dates = eachDayOfInterval({
      start: isBefore(startDate, weekStart) ? weekStart : startDate,
      end: isBefore(weekEnd, endDate) ? weekEnd : endDate,
    });
    const datesWithinView = dates.filter((date) =>
      payload.daysOfWeek.includes(date.getDay())
    );
    if (datesWithinView.length) {
      const placeholderBase = {
        ...planPlaceholderBase,
        companyId: payload.companyId,
        projectId: payload.projectId,
        taskId: payload.taskId,
        comment: payload.comment,
        // Placeholder data
        companyIds: [payload.companyId],
        createdByUserId: currentUserId,
        modifiedByUserId: currentUserId,
        refId: 'placeholder',
        taskName: payload.taskName,
        taskNumber: payload.taskNumber,
      };

      if (isUserPlanPayload(payload)) {
        mutateUserPlan({
          userId: payload.userId,
          projectId: payload.projectId,
          week,
          year,
          data: (data) => {
            const newData = data && structuredClone(data);
            if (newData) {
              datesWithinView.forEach((date) => {
                const dateKey = getPlannerDateString(date);
                if (
                  newData[0].plans[dateKey].projectPlans.some(
                    (existingPlan) => existingPlan.taskId === payload.taskId
                  )
                ) {
                  irrelevantDates.push(dateKey);
                } else {
                  newData[0].plans[dateKey].projectPlans.push({
                    ...placeholderBase,
                    date: dateKey,
                    userId: payload.userId,
                    machineIds: payload.machineIds,
                    roles: payload.roles,
                    isUserOnLoan: false,
                  });
                }
              });
            }

            return newData;
          },
        });
      } else {
        mutateMachinePlan({
          machineId: payload.machineId,
          projectId: payload.projectId,
          week,
          year,
          data: (data) => {
            const newData = data && structuredClone(data);
            if (newData) {
              datesWithinView.forEach((date) => {
                const dateKey = getPlannerDateString(date);
                if (
                  newData[0].plans[dateKey].projectPlans.some(
                    (existingPlan) => existingPlan.taskId === payload.taskId
                  )
                ) {
                  irrelevantDates.push(dateKey);
                } else {
                  newData[0].plans[dateKey].projectPlans.push({
                    ...placeholderBase,
                    date: dateKey,
                    machineId: payload.machineId,
                    isMachineOnLoan: false,
                    machineName: '',
                    machineNumber: '',
                  });
                }
              });
            }

            return newData;
          },
        });
      }
    }
  }

  if (irrelevantDates?.length) {
    showPlanningErrorToast(
      t('ResourcePlanning.NameAlreadyHasTaskPlanned', {
        name: isUserPlanPayload(payload)
          ? payload.userName
          : payload.machineName,
        taskName: payload.taskName,
        date: irrelevantDates
          .map((date) => localeFormat(parseISO(date)))
          .join(', '),
      })
    );
  }
};

export const useCreateRepeatedPlan = () => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUserAndSettings();
  const { week, year } = useSchedulerParams();
  const handleError = useHandlePlanningError();

  return async (payload: NewRepeatedPlanPayload, projectId: string) => {
    try {
      createRepeatedPlaceholders(t, payload, year, week, currentUser.id);

      const response = checkStatus(
        await post(`${baseUrl}/create-repeated`, payload)
      );
      const newPlans: UserPlan[] = await response.json();
      showPlanningToast(t('ResourcePlanning.Toasts.SequenceCreated'));
      mutateUserAndMachinePlans({ t, newPlans, payload, projectId });
    } catch (e) {
      mutateUserPlan({
        userId: payload.userId,
        projectId,
        week,
        year,
        revalidation: true,
      });
      handleError(e);
    }
  };
};

// Exported to be used in monthlyResourcePlanning.ts
export type UpdateRepeatedPlanArgs = {
  refId: string;
  projectId: string;
  week: number;
  year: number;
} & (
  | {
      payload: Omit<
        RepeatedPlanPayload,
        'daysOfWeek' | 'dates' | 'startDate' | 'endDate'
      > & {
        startDate?: RepeatedPlanPayload['startDate'];
      };
      includePastPlans: true;
    }
  | {
      payload: Omit<RepeatedPlanPayload, 'daysOfWeek' | 'dates' | 'endDate'>;
      includePastPlans: false;
    }
);

export const useUpdateRepeatedPlan = () => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();

  return async ({
    refId,
    payload,
    includePastPlans,
    projectId,
    week,
    year,
  }: UpdateRepeatedPlanArgs) => {
    try {
      const originalMachineIds: string[] = [];
      // Moving current week's repeated plans into inProgress state
      mutateUserPlan({
        userId: payload.userId,
        projectId,
        week,
        year,
        data: (data) => {
          const newData = data && structuredClone(data);
          if (newData) {
            originalMachineIds.push(
              ...Object.values(newData[0].plans)[0].projectPlans[0].machineIds
            );

            Object.entries(newData[0].plans).forEach(([dateKey, plans]) => {
              if (
                plans.projectPlans.some(
                  (projectPlan) => projectPlan.refId === refId
                )
              ) {
                newData[0].plans[dateKey] = {
                  ...newData[0].plans[dateKey],
                  projectPlans: newData[0].plans[dateKey].projectPlans.map(
                    (projectPlan) => {
                      if (
                        projectPlan.refId !== refId ||
                        (payload.startDate &&
                          isBefore(
                            parseISO(projectPlan.date),
                            parseISO(payload.startDate)
                          ))
                      ) {
                        // If start date was provided then we only need to move plans after it into inProgress state
                        return projectPlan;
                      }

                      return {
                        ...projectPlan,
                        ...payload,
                        id: null,
                      };
                    }
                  ),
                };
              }
            });
          }

          return newData;
        },
        revalidation: false,
      });
      checkStatus(
        await put(
          `${baseUrl}/update-repeated${includePastPlans ? '?includePastPlans=true' : ''}`,
          { ...payload, refId }
        )
      );
      if (includePastPlans) {
        showPlanningToast(t('ResourcePlanning.Toasts.SequenceUpdated'));
      } else {
        showPlanningToast(
          t('ResourcePlanning.Toasts.SequenceUpdatedFrom', {
            date: localeFormat(parseISO(payload.startDate), 'PP'),
          })
        );
      }
      mutateUserPlan({
        userId: payload.userId,
        projectId,
        week,
        year,
        data: (data) => data,
        revalidation: true,
      });

      const machinesIdsForRevalidation = originalMachineIds
        .filter((id) => !payload.machineIds.includes(id))
        .concat(
          payload.machineIds.filter((id) => !originalMachineIds.includes(id))
        );

      // Invalidating any plans of updated plan's user
      const query = getQueryString(week, year);
      mutate(
        (key) =>
          typeof key === 'string' &&
          key.startsWith(
            `${baseUrl}/project/${projectId}/users/${payload.userId}`
          ) &&
          !key.includes(query),
        undefined,
        false
      );

      // Invalidating any plans of relevant machines
      machinesIdsForRevalidation.forEach((machineId) =>
        mutate(
          (key) =>
            typeof key === 'string' &&
            key.startsWith(
              `${machineBaseUrl}/project/${projectId}/machines/${machineId}`
            ) &&
            !key.includes(query),
          undefined,
          false
        )
      );
    } catch (e) {
      mutateUserPlan({
        userId: payload.userId,
        projectId,
        week,
        year,
        revalidation: true,
      });
      handleError(e);
    }
  };
};

const removeSequence = (
  data: UserForPlanning[],
  plan: UserPlan,
  deleteWholeSequence: boolean
) => [
  {
    ...data[0],
    plans: Object.values(data[0].plans).reduce(
      (pv, dayPlan) => {
        if (
          !deleteWholeSequence &&
          isBefore(
            parseISO(dayPlan.date),
            parseISO(getPlannerDateString(plan.date))
          )
        ) {
          pv[dayPlan.date] = dayPlan;
        } else {
          pv[dayPlan.date] = {
            ...dayPlan,
            projectPlans: dayPlan.projectPlans.filter(
              (singlePlan) => singlePlan.refId !== plan.refId
            ),
          };
        }

        return pv;
      },
      {} as UserForPlanning['plans']
    ),
  },
];

export const useDeleteRepeatedPlan = () => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();
  const projectId = useAtomValue(projectAtom);

  return async (plan: UserPlan, deleteWholeSequence: boolean) => {
    const { userId, refId, date } = plan;

    try {
      // Removing plans with the same refId from all cached user plans
      mutate<UserForPlanning[]>(
        (key) =>
          typeof key === 'string' &&
          key.includes(`${baseUrl}/project/${projectId}/users/${userId}`),
        (data) => data && removeSequence(data, plan, deleteWholeSequence),
        false
      );
      checkStatus(
        await del(
          `${baseUrl}/delete-repeated`,
          deleteWholeSequence
            ? {
                refId,
              }
            : { refId, startDate: date }
        )
      );
      if (deleteWholeSequence) {
        showPlanningToast(t('ResourcePlanning.Toasts.SequenceDeleted'));
      } else {
        showPlanningToast(
          t('ResourcePlanning.Toasts.SequenceDeletedFrom', {
            date: localeFormat(parseISO(plan.date), 'PP'),
          })
        );
      }

      // Invalidating related machine plans
      plan.machineIds.forEach((machineId) =>
        mutate<UserForPlanning[]>(
          (key) =>
            typeof key === 'string' &&
            key.includes(
              `${machineBaseUrl}/project/${projectId}/machines/${machineId}`
            ),
          (data) => data && removeSequence(data, plan, deleteWholeSequence)
        )
      );
    } catch (e) {
      handleError(e);
      // Invalidating all relevant plans if something went wrong
      mutate<UserForPlanning[]>(
        (key) =>
          typeof key === 'string' &&
          key.includes(`${baseUrl}/project/${projectId}/users/${userId}`),
        undefined,
        true
      );
      plan.machineIds.forEach((machineId) =>
        mutate<UserForPlanning[]>(
          (key) =>
            typeof key === 'string' &&
            key.includes(
              `${machineBaseUrl}/project/${projectId}/machines/${machineId}`
            ),
          undefined,
          true
        )
      );
    }
  };
};

// Exported to be used in monthlyResourcePlanning.ts
export const isExistingMachinePlan = (
  plan:
    | Omit<RepeatedMachinePlanPayload, 'daysOfWeek'>
    | Omit<RepeatedMachinePlanPayload, 'dates'>
    | NewMachineTask
    | MachinePlan
): plan is MachinePlan => !!(plan as MachinePlan).id;

// Exported to be used in monthlyResourcePlanning.ts
export interface MachinePlanMutationArgs {
  t: TFunction;
  skipDuplicateCheck?: boolean;
  newPlans: MachinePlan[];
  payload:
    | Omit<RepeatedMachinePlanPayload, 'daysOfWeek'>
    | Omit<RepeatedMachinePlanPayload, 'dates'>
    | NewMachineTask
    | MachinePlan;
  projectId: string;
  isBeforeApiCall?: boolean;
  originalDate?: string;
  originalId?: string | null;
  originalMachineId?: string;
}

// Exported to be used in monthlyResourcePlanning.ts
export const mutateMachinePlans = async ({
  t,
  skipDuplicateCheck,
  newPlans,
  payload,
  projectId,
  isBeforeApiCall,
  originalDate,
  originalId,
  originalMachineId,
}: MachinePlanMutationArgs) => {
  if (!newPlans.length) {
    return {};
  }

  const { machineId } = payload;

  const oldDate = originalDate && parseISO(originalDate);
  const newDate = parseISO(newPlans[0].date);

  const plansByWeekAndYear = newPlans.reduce(
    (pv, cv) => {
      const date = parseISO(cv.date);
      const key = `${getYear(date)}-${getWeekForPlanning(date)}`;
      if (pv[key]) {
        pv[key].push(cv);
      } else {
        pv[key] = [cv];
      }

      return pv;
    },
    {} as { [weekAndYear: string]: MachinePlan[] }
  );

  // Filtering out dates where task is already planned (relevant for planning on multiple dates)
  const relevantDates: string[] = [];
  const irrelevantDates: string[] = [];

  await Promise.all(
    Object.entries(plansByWeekAndYear).map(async ([weekAndYear, plans]) => {
      const [year, week] = weekAndYear.split('-');

      await mutateMachinePlan({
        machineId,
        projectId,
        week: Number(week),
        year: Number(year),
        data: (data) => {
          const newData = data && structuredClone(data);
          if (newData) {
            plans.forEach((plan) => {
              const dateKey = plan.date.replace('Z', '');
              if (plan.id) {
                // Filtering out placeholders when actual plans arrived
                newData[0].plans[dateKey].projectPlans = newData[0].plans[
                  dateKey
                ].projectPlans.filter((projectPlan) => projectPlan.id);
                // Adding a new plan
                newData[0].plans[dateKey].projectPlans.push(plan);
              } else if (
                !skipDuplicateCheck &&
                newData[0].plans[dateKey].projectPlans.some(
                  (existingPlan) => existingPlan.taskId === plan.taskId
                )
              ) {
                if (newPlans.length === 1) {
                  throw new Error(
                    t('ResourcePlanning.NameAlreadyHasTaskPlanned', {
                      name: newData[0].machineName,
                      taskName: plan.taskName,
                      date: localeFormat(parseISO(plan.date)),
                    })
                  );
                } else {
                  irrelevantDates.push(plan.date);
                }
              } else {
                relevantDates.push(plan.date);

                if (
                  !originalMachineId &&
                  oldDate &&
                  isSameDay(oldDate, newDate)
                ) {
                  // Updating a plan if it wasn't moved to a different date
                  newData[0].plans[dateKey].projectPlans = newData[0].plans[
                    dateKey
                  ].projectPlans.map((projectPlan) =>
                    projectPlan.id === originalId ? newPlans[0] : projectPlan
                  );
                } else {
                  // Adding a new plan
                  newData[0].plans[dateKey].projectPlans.push(plan);
                }
              }
            });
          } else {
            // If newData is missing it means that plans for that week were not fetched yet
            // so it is up to backend to decide if new plan should be created or not
            plans.forEach((plan) => {
              relevantDates.push(plan.date);
            });
          }

          return newData;
        },
        revalidation: false,
      });
    })
  );

  if (isBeforeApiCall && oldDate && isExistingMachinePlan(payload)) {
    // If original date is provided and payload is an existing plan
    if (!isSameDay(oldDate, newDate)) {
      // If plan's date was changed
      await mutateMachinePlan({
        machineId: originalMachineId || machineId,
        projectId,
        week: getWeekForPlanning(oldDate),
        year: getYear(oldDate),
        data: (data) => {
          // Removing plan that was moved to a different date
          const newData = data && structuredClone(data);
          if (newData) {
            const dateKey = getPlannerDateString(originalDate);
            newData[0].plans[dateKey].projectPlans = newData[0].plans[
              dateKey
            ].projectPlans.filter((plan) => plan.id !== payload.id);
          }

          return newData;
        },
        revalidation: false,
      });
    } else if (originalMachineId && machineId !== originalMachineId) {
      // If date wasn't changed but plan was moved to a different machine
      await mutateMachinePlan({
        machineId: originalMachineId,
        projectId,
        week: getWeekForPlanning(oldDate),
        year: getYear(oldDate),
        data: (data) => {
          // Removing plan that was moved to a different date
          const newData = data && structuredClone(data);
          if (newData) {
            const dateKey = getPlannerDateString(originalDate);
            newData[0].plans[dateKey].projectPlans = newData[0].plans[
              dateKey
            ].projectPlans.filter((plan) => plan.id !== payload.id);
          }

          return newData;
        },
        revalidation: false,
      });
    }
  }

  return { relevantDates, irrelevantDates };
};

export interface MachinePlanWithOriginalDate {
  plan: MachinePlan;
  originalDate: string;
}

// Exported to be used in monthlyResourcePlanning.ts
export const isRegularMachinePlansArray = (
  plan: MachinePlan[] | MachinePlanWithOriginalDate[]
): plan is MachinePlan[] => !!(plan as MachinePlan[])[0].id;

// Exported to be used in monthlyResourcePlanning.ts
export interface MultiMachinePlanMutationArgs {
  newPlans: MachinePlan[] | MachinePlanWithOriginalDate[];
  creatingPlaceholders: boolean;
  projectId: string;
}

// Exported to be used in monthlyResourcePlanning.ts
export const mutateMultipleMachinePlans = ({
  newPlans,
  creatingPlaceholders,
  projectId,
}: MultiMachinePlanMutationArgs) => {
  if (!newPlans.length) {
    return;
  }

  if (!isRegularMachinePlansArray(newPlans) && creatingPlaceholders) {
    // If plans were moved to new dates we need to remove plans from original dates
    const originalDate = parseISO(newPlans[0].originalDate);
    const year = getYear(originalDate);
    const week = getWeekForPlanning(originalDate);

    const plansByMachineId = newPlans.reduce(
      (pv, cv) => {
        if (pv[cv.plan.machineId]) {
          pv[cv.plan.machineId].push({
            ...cv.plan,
            date: cv.originalDate,
          });
        } else {
          pv[cv.plan.machineId] = [
            {
              ...cv.plan,
              date: cv.originalDate,
            },
          ];
        }

        return pv;
      },
      {} as { [machineId: string]: MachinePlan[] }
    );

    Object.entries(plansByMachineId).map(async ([machineId, plans]) => {
      mutateMachinePlan({
        machineId,
        projectId,
        week: Number(week),
        year: Number(year),
        data: (data) => {
          const newData = data && structuredClone(data);
          if (newData) {
            plans.forEach((newPlan) => {
              const dateKey = newPlan.date.replace('Z', '');
              if (creatingPlaceholders) {
                newData[0].plans[dateKey].projectPlans = newData[0].plans[
                  dateKey
                ].projectPlans.filter((plan) => plan.taskId !== newPlan.taskId);
              }
            });
          }

          return newData;
        },
        revalidation: false,
      });
    });
  }

  const updatedPlans = isRegularMachinePlansArray(newPlans)
    ? newPlans
    : newPlans.map(({ plan }) => plan);

  const year = getYear(parseISO(updatedPlans[0].date));
  const week = getWeekForPlanning(parseISO(updatedPlans[0].date));

  const plansByMachineId = updatedPlans.reduce(
    (pv, cv) => {
      if (pv[cv.machineId]) {
        pv[cv.machineId].push(cv);
      } else {
        pv[cv.machineId] = [cv];
      }

      return pv;
    },
    {} as { [userId: string]: MachinePlan[] }
  );

  Object.entries(plansByMachineId).map(async ([machineId, plans]) => {
    mutateMachinePlan({
      machineId,
      projectId,
      week: Number(week),
      year: Number(year),
      data: (data) => {
        const newData = data && structuredClone(data);
        if (newData) {
          plans.forEach((newPlan) => {
            const dateKey = newPlan.date.replace('Z', '');
            if (creatingPlaceholders) {
              if (
                newData[0].plans[dateKey].projectPlans.find(
                  (plan) => plan.id === newPlan.id
                )
              ) {
                newData[0].plans[dateKey].projectPlans = newData[0].plans[
                  dateKey
                ].projectPlans.map((plan) =>
                  plan.id === newPlan.id
                    ? {
                        ...newPlan,
                        id: null,
                      }
                    : plan
                );
              } else {
                /* If plan with matching ID is missing on a day
                then it means that new plans are being created
                (e.g. plans were moved or copied) */
                newData[0].plans[dateKey].projectPlans.push({
                  ...newPlan,
                  id: null,
                  refId: null,
                });
              }
            } else {
              newData[0].plans[dateKey].projectPlans = newData[0].plans[
                dateKey
                // Filtering out placeholder plans
              ].projectPlans.filter((plan) => plan.id);
              newData[0].plans[dateKey].projectPlans.push(newPlan);
            }
          });
        }

        return newData;
      },
      revalidation: false,
    });
  });
};

// Exported to be used in monthlyResourcePlanning.ts
export interface CreateOrUpdateMachinePlanArgs {
  payload: NewMachineTask | MachinePlan;
  projectId: string;
  skipDuplicateCheck?: boolean;
  originalDate?: string;
  originalMachineId?: string;
}

export const useCreateOrUpdateMachinePlan = () => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();

  return async ({
    payload,
    projectId,
    skipDuplicateCheck,
    originalDate,
    originalMachineId,
  }: CreateOrUpdateMachinePlanArgs) => {
    try {
      // Adding placeholder plan
      await mutateMachinePlans({
        t,
        skipDuplicateCheck,
        newPlans: [{ ...payload, id: null, refId: null } as MachinePlan],
        payload,
        projectId,
        isBeforeApiCall: true,
        originalDate,
        originalId: payload.id,
        originalMachineId,
      });
      const response = checkStatus(
        await post(`${machineBaseUrl}/create-or-update`, [payload])
      );
      if (payload.id) {
        if (
          (originalDate &&
            !isSameDay(parseISO(originalDate), parseISO(payload.date))) ||
          originalMachineId
        ) {
          showPlanningToast(t('ResourcePlanning.Toasts.Moved', { count: 1 }));
        } else {
          showPlanningToast(t('ResourcePlanning.Toasts.Updated', { count: 1 }));
        }
      } else {
        showPlanningToast(t('ResourcePlanning.Toasts.Created'));
      }
      const newPlans: MachinePlan[] = await response.json();
      mutateMachinePlans({ t, newPlans, payload, projectId, originalDate });
    } catch (e) {
      mutateMachinePlan({
        machineId: payload.machineId,
        projectId,
        week: getWeekForPlanning(parseISO(payload.date)),
        year: parseISO(payload.date).getFullYear(),
        revalidation: true,
      });
      handleError(e);
    }
  };
};

export const useCreateMultipleMachinePlans = () => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();

  return async (payload: MachinePlan[], projectId: string) => {
    try {
      mutateMultipleMachinePlans({
        newPlans: payload.map((plan) => ({
          plan: { ...plan, id: null },
          originalDate: plan.date,
        })),
        creatingPlaceholders: true,
        projectId,
      });
      const response = checkStatus(
        await post(
          `${machineBaseUrl}/create-or-update`,
          payload.map((plan) => ({
            ...plan,
            id: null,
          }))
        )
      );
      const newPlans: MachinePlan[] = await response.json();
      showPlanningToast(
        t('ResourcePlanning.Toasts.Created', { count: newPlans.length })
      );
      mutateMultipleMachinePlans({
        newPlans,
        creatingPlaceholders: false,
        projectId,
      });
    } catch (e) {
      mutateMachinePlan({
        machineId: payload[0].machineId,
        projectId,
        week: getWeekForPlanning(parseISO(payload[0].date)),
        year: parseISO(payload[0].date).getFullYear(),
        revalidation: true,
      });
      handleError(e);
    }
  };
};

// Exported to be used in monthlyResourcePlanning.ts
export interface UpdateMultipleMachinePlansArgs {
  payload: MachinePlan[] | MachinePlanWithOriginalDate[];
  projectId: string;
}

export const useUpdateMultipleMachinePlans = () => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();

  return async ({ payload, projectId }: UpdateMultipleMachinePlansArgs) => {
    try {
      mutateMultipleMachinePlans({
        newPlans: payload,
        creatingPlaceholders: true,
        projectId,
      });
      const response = checkStatus(
        await post(
          `${machineBaseUrl}/create-or-update`,
          isRegularMachinePlansArray(payload)
            ? payload
            : payload.map(({ plan }) => plan)
        )
      );
      const newPlans: MachinePlan[] = await response.json();
      showPlanningToast(
        t('ResourcePlanning.Toasts.Updated', { count: newPlans.length })
      );
      mutateMultipleMachinePlans({
        newPlans,
        creatingPlaceholders: false,
        projectId,
      });
    } catch (e) {
      const datesByMachineId: { [machineId: string]: Set<string> } = {};
      const updateDates = (plan: MachinePlan) => {
        if (datesByMachineId[plan.machineId]) {
          datesByMachineId[plan.machineId].add(plan.date);
        } else {
          datesByMachineId[plan.machineId] = new Set(plan.date);
        }
      };

      if (isRegularMachinePlansArray(payload)) {
        payload.forEach((plan) => updateDates(plan));
      } else {
        payload.forEach(({ plan }) => updateDates(plan));
      }

      Object.entries(datesByMachineId).forEach(([machineId, dates]) => {
        Array.from(dates).forEach((date) => {
          mutateMachinePlan({
            machineId,
            projectId,
            week: getWeekForPlanning(parseISO(date)),
            year: parseISO(date).getFullYear(),
            revalidation: true,
          });
        });
      });
      handleError(e);
    }
  };
};

export const useCreateMachinePlanOnDates = () => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUserAndSettings();
  const handleError = useHandlePlanningError();

  return async (
    payload: Omit<RepeatedMachinePlanPayload, 'daysOfWeek'>,
    projectId: string
  ) => {
    try {
      // Creating placeholders
      const { relevantDates, irrelevantDates } = await mutateMachinePlans({
        t,
        newPlans: payload.dates.map((date) => ({
          ...planPlaceholderBase,
          machineId: payload.machineId,
          isMachineOnLoan: false,
          date,
          companyId: payload.companyId,
          projectId: payload.projectId,
          taskId: payload.taskId,
          comment: payload.comment,
          // Placeholder data
          companyIds: [payload.companyId],
          createdByUserId: currentUser.id,
          modifiedByUserId: currentUser.id,
          refId: null,
          taskName: payload.taskName,
          taskNumber: payload.taskNumber,
          machineName: '',
          machineNumber: '',
          moveReason: payload.moveReason,
          moveCategory: payload.moveCategory,
          moveCause: payload.moveCause,
        })),
        payload,
        projectId,
        isBeforeApiCall: true,
      });

      if (irrelevantDates?.length) {
        showPlanningErrorToast(
          t('ResourcePlanning.NameAlreadyHasTaskPlanned', {
            name: payload.machineName,
            taskName: payload.taskName,
            date: irrelevantDates
              .map((date) => localeFormat(parseISO(date)))
              .join(', '),
          })
        );
      }

      const response = checkStatus(
        await post(`${machineBaseUrl}/create-multiple-days`, {
          ...payload,
          createOnHoliday: true,
          dates: relevantDates,
        })
      );
      const newPlans: MachinePlan[] = await response.json();
      showPlanningToast(
        t('ResourcePlanning.Toasts.Created', { count: newPlans.length })
      );
      mutateMachinePlans({ t, newPlans, payload, projectId });
    } catch (e) {
      const weeksByYear: { [year: number]: Set<number> } = {};
      payload.dates.forEach((date) => {
        const year = parseISO(date).getFullYear();
        if (weeksByYear[year]) {
          weeksByYear[year].add(getWeekForPlanning(parseISO(date)));
        } else {
          weeksByYear[year] = new Set([getWeekForPlanning(parseISO(date))]);
        }
      });
      Object.entries(weeksByYear).forEach(([year, weeks]) => {
        Array.from(weeks).forEach((week) => {
          mutateMachinePlan({
            machineId: payload.machineId,
            projectId,
            week,
            year: Number(year),
            revalidation: true,
          });
        });
      });

      handleError(e);
    }
  };
};

export const useCreateRepeatedMachinePlan = () => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUserAndSettings();
  const { week, year } = useSchedulerParams();
  const handleError = useHandlePlanningError();

  return async (payload: NewRepeatedMachinePlanPayload, projectId: string) => {
    try {
      createRepeatedPlaceholders(t, payload, year, week, currentUser.id);

      const response = checkStatus(
        await post(`${machineBaseUrl}/create-repeated`, payload)
      );
      const newPlans: MachinePlan[] = await response.json();
      showPlanningToast(t('ResourcePlanning.Toasts.SequenceCreated'));
      mutateMachinePlans({ t, newPlans, payload, projectId });
    } catch (e) {
      mutateMachinePlan({
        machineId: payload.machineId,
        projectId,
        week,
        year,
        revalidation: true,
      });
      handleError(e);
    }
  };
};

type UpdateRepeatedMachinePlanArgs = {
  refId: string;
  projectId: string;
  week: number;
  year: number;
} & (
  | {
      payload: Omit<
        RepeatedMachinePlanPayload,
        'daysOfWeek' | 'dates' | 'startDate' | 'endDate'
      > & {
        startDate?: RepeatedMachinePlanPayload['startDate'];
      };
      includePastPlans: true;
    }
  | {
      payload: Omit<
        RepeatedMachinePlanPayload,
        'daysOfWeek' | 'dates' | 'endDate'
      >;
      includePastPlans: false;
    }
);

export const useUpdateRepeatedMachinePlan = () => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();

  return async ({
    refId,
    payload,
    includePastPlans,
    projectId,
    week,
    year,
  }: UpdateRepeatedMachinePlanArgs) => {
    try {
      // Moving current week's repeated plans into inProgress state
      mutateMachinePlan({
        machineId: payload.machineId,
        projectId,
        week,
        year,
        data: (data) => {
          const newData = data && structuredClone(data);
          if (newData) {
            Object.entries(newData[0].plans).forEach(([dateKey, plans]) => {
              if (
                plans.projectPlans.some(
                  (projectPlan) => projectPlan.refId === refId
                )
              ) {
                newData[0].plans[dateKey] = {
                  ...newData[0].plans[dateKey],
                  projectPlans: newData[0].plans[dateKey].projectPlans.map(
                    (projectPlan) => {
                      if (
                        projectPlan.refId !== refId ||
                        (payload.startDate &&
                          isBefore(
                            parseISO(projectPlan.date),
                            parseISO(payload.startDate)
                          ))
                      ) {
                        // If start date was provided then we only need to move plans after it into inProgress state
                        return projectPlan;
                      }

                      return {
                        ...projectPlan,
                        ...payload,
                        id: null,
                      };
                    }
                  ),
                };
              }
            });
          }

          return newData;
        },
        revalidation: false,
      });
      checkStatus(
        await put(
          `${machineBaseUrl}/update-repeated${includePastPlans ? '?includePastPlans=true' : ''}`,
          { ...payload, refId }
        )
      );
      if (includePastPlans) {
        showPlanningToast(t('ResourcePlanning.Toasts.SequenceUpdated'));
      } else {
        showPlanningToast(
          t('ResourcePlanning.Toasts.SequenceUpdatedFrom', {
            date: localeFormat(parseISO(payload.startDate), 'PP'),
          })
        );
      }
      mutateMachinePlan({
        machineId: payload.machineId,
        projectId,
        week,
        year,
        data: (data) => data,
        revalidation: true,
      });

      // Invalidating any plans of updated plan's user
      const query = getQueryString(week, year);
      mutate(
        (key) =>
          typeof key === 'string' &&
          key.startsWith(
            `${machineBaseUrl}/project/${projectId}/machines/${payload.machineId}`
          ) &&
          !key.includes(query),
        undefined,
        false
      );
    } catch (e) {
      mutateMachinePlan({
        machineId: payload.machineId,
        projectId,
        week,
        year,
        revalidation: true,
      });
      handleError(e);
    }
  };
};

const removeMachineSequence = (
  data: MachineForPlanning[],
  plan: MachinePlan,
  deleteWholeSequence: boolean
) => [
  {
    ...data[0],
    plans: Object.values(data[0].plans).reduce(
      (pv, dayPlan) => {
        if (
          !deleteWholeSequence &&
          isBefore(
            parseISO(dayPlan.date),
            parseISO(getPlannerDateString(plan.date))
          )
        ) {
          pv[dayPlan.date] = dayPlan;
        } else {
          pv[dayPlan.date] = {
            ...dayPlan,
            projectPlans: dayPlan.projectPlans.filter(
              (singlePlan) => singlePlan.refId !== plan.refId
            ),
          };
        }

        return pv;
      },
      {} as MachineForPlanning['plans']
    ),
  },
];

export const useDeleteRepeatedMachinePlan = () => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();
  const projectId = useAtomValue(projectAtom);

  return async (plan: MachinePlan, deleteWholeSequence: boolean) => {
    const { machineId, refId, date } = plan;

    try {
      // Removing plans with the same refId from all cached user plans
      mutate<MachineForPlanning[]>(
        (key) =>
          typeof key === 'string' &&
          key.includes(
            `${machineBaseUrl}/project/${projectId}/machines/${machineId}`
          ),
        (data) =>
          data && removeMachineSequence(data, plan, deleteWholeSequence),
        false
      );
      checkStatus(
        await del(
          `${machineBaseUrl}/delete-repeated`,
          deleteWholeSequence
            ? {
                refId,
              }
            : { refId, startDate: date }
        )
      );
      if (deleteWholeSequence) {
        showPlanningToast(t('ResourcePlanning.Toasts.SequenceDeleted'));
      } else {
        showPlanningToast(
          t('ResourcePlanning.Toasts.SequenceDeletedFrom', {
            date: localeFormat(parseISO(plan.date), 'PP'),
          })
        );
      }
    } catch (e) {
      handleError(e);
      // Invalidating all relevant plans if something went wrong
      mutate<UserForPlanning[]>(
        (key) =>
          typeof key === 'string' &&
          key.includes(
            `${machineBaseUrl}/project/${projectId}/machines/${machineId}`
          ),
        undefined,
        true
      );
    }
  };
};

export const useDeletePlan = () => {
  const { t } = useTranslation();
  const { week, year } = useSchedulerParams();
  const projectId = useAtomValue(projectAtom);

  const handleError = useHandlePlanningError();

  return async (planToDelete: UserPlan): Promise<void> => {
    const { userId } = planToDelete;
    const machineIdsToInvalidate: string[] = planToDelete.machineIds;

    await mutateUserPlan({
      userId,
      projectId,
      week,
      year,
      data: async (data) => {
        try {
          checkStatus(await del(`${baseUrl}/${planToDelete.id}`));
          showPlanningToast(t('ResourcePlanning.Toasts.Deleted', { count: 1 }));

          const newData = data && structuredClone(data);
          if (newData) {
            const dateKey = getPlannerDateString(planToDelete.date);
            newData[0].plans[dateKey].projectPlans = newData[0].plans[
              dateKey
            ].projectPlans.filter((plan) => plan.id !== planToDelete.id);
          }

          return newData;
        } catch (e) {
          handleError(e);
          return data;
        }
      },
      revalidation: {
        revalidate: false,
        optimisticData: (data) => {
          const newData = data && structuredClone(data);
          if (newData) {
            const dateKey = getPlannerDateString(planToDelete.date);
            newData[0].plans[dateKey].projectPlans = newData[0].plans[
              dateKey
            ].projectPlans.filter((plan) => plan.id !== planToDelete.id);
          }

          return newData || [];
        },
      },
    });

    machineIdsToInvalidate.forEach((machineId) =>
      mutateMachinePlan({
        machineId,
        projectId,
        week,
        year,
        data: null,
        revalidation: true,
      })
    );
  };
};

export const useDeleteMachinePlan = () => {
  const { t } = useTranslation();
  const { week, year } = useSchedulerParams();
  const projectId = useAtomValue(projectAtom);

  const handleError = useHandlePlanningError();

  return async (planToDelete: MachinePlan): Promise<void> => {
    const { machineId } = planToDelete;

    await mutateMachinePlan({
      machineId,
      projectId,
      week,
      year,
      data: async (data) => {
        try {
          checkStatus(await del(`${machineBaseUrl}/${planToDelete.id}`));
          showPlanningToast(t('ResourcePlanning.Toasts.Deleted', { count: 1 }));

          const newData = data && structuredClone(data);
          if (newData) {
            const dateKey = getPlannerDateString(planToDelete.date);
            newData[0].plans[dateKey].projectPlans = newData[0].plans[
              dateKey
            ].projectPlans.filter((plan) => plan.id !== planToDelete.id);
          }

          return newData;
        } catch (e) {
          handleError(e);
          return data;
        }
      },
      revalidation: {
        revalidate: false,
        optimisticData: (data) => {
          const newData = data && structuredClone(data);
          if (newData) {
            const dateKey = getPlannerDateString(planToDelete.date);
            newData[0].plans[dateKey].projectPlans = newData[0].plans[
              dateKey
            ].projectPlans.filter((plan) => plan.id !== planToDelete.id);
          }

          return newData || [];
        },
      },
    });
  };
};

export const useBulkPlanDelete = () => {
  const { t } = useTranslation();
  const projectId = useAtomValue(projectAtom);

  const handleError = useHandlePlanningError();

  return async (plans: UserPlan[]): Promise<void> => {
    const machineIds = {} as { [year: string]: { [week: string]: string[] } };

    const mappedPlans = plans.reduce(
      (pv, cv) => {
        const date = parseISO(cv.date);
        const year = getYear(date);
        const week = getWeekForPlanning(date);
        if (pv[cv.userId]) {
          if (pv[cv.userId][year]) {
            if (pv[cv.userId][year][week]) {
              pv[cv.userId][year][week].push(cv);
            } else {
              pv[cv.userId][year][week] = [cv];
            }
          } else {
            pv[cv.userId][year] = { [week]: [cv] };
          }
        } else {
          pv[cv.userId] = { [year]: { [week]: [cv] } };
        }

        if (cv.machineIds.length) {
          if (machineIds[year]) {
            if (machineIds[year][week]) {
              machineIds[year][week].push(...cv.machineIds);
            } else {
              machineIds[year][week] = cv.machineIds;
            }
          } else {
            machineIds[year] = { [week]: cv.machineIds };
          }
        }

        return pv;
      },
      {} as {
        [userId: string]: { [year: string]: { [week: string]: UserPlan[] } };
      }
    );

    // Optimistically removing deleted plans
    Object.entries(mappedPlans).forEach(([userId, plansByYears]) => {
      Object.entries(plansByYears).forEach(([year, plansByWeeks]) => {
        Object.entries(plansByWeeks).forEach(([week, plansToDelete]) => {
          plansToDelete.forEach((planToDelete) => {
            mutateUserPlan({
              userId,
              projectId,
              week: Number(week),
              year: Number(year),
              data: (data) => {
                const newData = data && structuredClone(data);
                if (newData) {
                  const dateKey = getPlannerDateString(planToDelete.date);
                  newData[0].plans[dateKey].projectPlans = newData[0].plans[
                    dateKey
                  ].projectPlans.filter((plan) => plan.id !== planToDelete.id);
                }

                return newData;
              },
              revalidation: false,
            });
          });
        });
      });
    });

    try {
      checkStatus(
        await del(
          `${baseUrl}`,
          plans.map((plan) => plan.id)
        )
      );
      showPlanningToast(
        t('ResourcePlanning.Toasts.Deleted', { count: plans.length })
      );
      // Invalidating relevant machine plan caches
      Object.entries(machineIds).forEach(([year, idsByWeeks]) => {
        Object.entries(idsByWeeks).forEach(([week, ids]) => {
          ids.forEach((machineId) => {
            mutateMachinePlan({
              machineId,
              projectId,
              week: Number(week),
              year: Number(year),
              data: null,
              revalidation: true,
            });
          });
        });
      });
    } catch (e) {
      Object.entries(mappedPlans).forEach(([userId, plansByYears]) => {
        Object.entries(plansByYears).forEach(([year, plansByWeeks]) => {
          Object.keys(plansByWeeks).forEach((week) => {
            mutateUserPlan({
              userId,
              projectId,
              week: Number(week),
              year: Number(year),
              revalidation: true,
            });
          });
        });
      });
      handleError(e);
    }
  };
};

export const useBulkMachinePlanDelete = () => {
  const { t } = useTranslation();
  const projectId = useAtomValue(projectAtom);

  const handleError = useHandlePlanningError();

  return async (plans: MachinePlan[]): Promise<void> => {
    const mappedPlans = plans.reduce(
      (pv, cv) => {
        const date = parseISO(cv.date);
        const year = getYear(date);
        const week = getWeekForPlanning(date);
        if (pv[cv.machineId]) {
          if (pv[cv.machineId][year]) {
            if (pv[cv.machineId][year][week]) {
              pv[cv.machineId][year][week].push(cv);
            } else {
              pv[cv.machineId][year][week] = [cv];
            }
          } else {
            pv[cv.machineId][year] = { [week]: [cv] };
          }
        } else {
          pv[cv.machineId] = { [year]: { [week]: [cv] } };
        }

        return pv;
      },
      {} as {
        [machineId: string]: {
          [year: string]: { [week: string]: MachinePlan[] };
        };
      }
    );

    // Optimistically removing deleted plans
    Object.entries(mappedPlans).forEach(([machineId, plansByYears]) => {
      Object.entries(plansByYears).forEach(([year, plansByWeeks]) => {
        Object.entries(plansByWeeks).forEach(([week, plansToDelete]) => {
          plansToDelete.forEach((planToDelete) => {
            mutateMachinePlan({
              machineId,
              projectId,
              week: Number(week),
              year: Number(year),
              data: (data) => {
                const newData = data && structuredClone(data);
                if (newData) {
                  const dateKey = getPlannerDateString(planToDelete.date);
                  newData[0].plans[dateKey].projectPlans = newData[0].plans[
                    dateKey
                  ].projectPlans.filter((plan) => plan.id !== planToDelete.id);
                }

                return newData;
              },
              revalidation: false,
            });
          });
        });
      });
    });

    try {
      checkStatus(
        await del(
          `${machineBaseUrl}`,
          plans.map((plan) => plan.id)
        )
      );
      showPlanningToast(
        t('ResourcePlanning.Toasts.Deleted', { count: plans.length })
      );
    } catch (e) {
      Object.entries(mappedPlans).forEach(([machineId, plansByYears]) => {
        Object.entries(plansByYears).forEach(([year, plansByWeeks]) => {
          Object.keys(plansByWeeks).forEach((week) => {
            mutateMachinePlan({
              machineId,
              projectId,
              week: Number(week),
              year: Number(year),
              revalidation: true,
            });
          });
        });
      });
      handleError(e);
    }
  };
};

interface RolesById {
  [key: string]: Role;
}

export const useRolesList = (companyId?: string, revalidateOnMount = true) => {
  const handleError = useHandlePlanningError();

  const { data, error, ...rest } = useSWR<Role[]>(
    companyId ? `api/project-plan/hseroles/company/${companyId}` : null,
    {
      revalidateOnFocus: false,
      revalidateOnMount,
    }
  );

  const isFetching = !data && !error;

  React.useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);

  const rolesById: RolesById = React.useMemo(
    () =>
      data
        ? data.reduce(
            (acc, role) => ({
              ...acc,
              [role.id]: role,
            }),
            {}
          )
        : {},
    [data]
  );

  return {
    rolesById,
    isFetching,
    error,
    roles: data,
    noRoles: !isFetching && !data?.length,
    ...rest,
  };
};

export const useAddUserForPlanning = () => {
  const handleError = useHandlePlanningError();

  return async (userId: string, projectId: string): Promise<void> => {
    try {
      await put(
        `api/project-plan/add-user-to-project-plan/user/${userId}/project/${projectId}`
      );
    } catch (e) {
      handleError(e);
    }
  };
};

export const useAddMachineForPlanning = () => {
  const handleError = useHandlePlanningError();

  return async (machineId: string, projectId: string): Promise<void> => {
    try {
      await put(
        `${machineBaseUrl}/add-machine-to-project-plan/machine/${machineId}/project/${projectId}`
      );
    } catch (e) {
      handleError(e);
    }
  };
};

export const useRemoveUserFromPlanning = () => {
  const handleError = useHandlePlanningError();

  const removeUser = React.useCallback(
    async (userId: string, projectId: string): Promise<void> => {
      try {
        await del(
          `api/project-plan/remove-user-from-project-plan/user/${userId}/project/${projectId}`
        );
      } catch (e) {
        handleError(e);
      }
    },
    []
  );

  return removeUser;
};

export const useRemoveMachineFromPlanning = () => {
  const handleError = useHandlePlanningError();

  return async (machineId: string, projectId: string): Promise<void> => {
    try {
      await del(
        `${machineBaseUrl}/remove-machine-from-project-plan/machine/${machineId}/project/${projectId}`
      );
    } catch (e) {
      handleError(e);
    }
  };
};

export const useAssignMachineToUser = () => {
  const handleError = useHandlePlanningError();

  return async (
    projectId: string,
    userId: string,
    machineId: string
  ): Promise<void | null> => {
    try {
      return checkStatus(
        await put(
          `${machineBaseUrl}/project/${projectId}/user/${userId}/machine/${machineId}`
        )
      );
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

export const useAssignMultipleMachinesToUser = () => {
  const handleError = useHandlePlanningError();

  return async (
    projectId: string,
    userId: string,
    machineIds: string[]
  ): Promise<void | null> => {
    try {
      return checkStatus(
        await put(
          `${machineBaseUrl}/project/${projectId}/user/${userId}/machine`,
          machineIds
        )
      );
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

export const useUnassignMachineFromUser = () => {
  const handleError = useHandlePlanningError();

  return async (
    projectId: string,
    userId: string,
    machineId: string
  ): Promise<void | null> => {
    try {
      return checkStatus(
        await del(
          `${machineBaseUrl}/project/${projectId}/user/${userId}/machine/${machineId}`
        )
      );
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

export const useGetUsersPlannedResources = (
  projectId: string,
  userIds: string[],
  revalidateOnMount = false
) => {
  const handleError = useHandlePlanningError();
  const params = userIds?.join('&userIds=');

  const { data, error, ...rest } = useSWR<AssignedResources>(
    projectId && userIds?.length > 0
      ? `${machineBaseUrl}/resources/project/${projectId}/users?userIds=${params}`
      : null,
    {
      revalidateOnFocus: false,
      revalidateOnMount,
    }
  );

  React.useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);

  return {
    resources: data,
    error,
    isFetching: !data && !error,
    ...rest,
  };
};

export const useGetTags = (revalidateOnMount = false) => {
  const { data, ...rest } = useApi<CompanyTag[]>(
    '/api/v3/tag?onlyExistingTags=true',
    {
      revalidateOnMount,
    }
  );

  return {
    tags: data,
    ...rest,
  };
};

interface SinglePlanArgs {
  projectId: string;
  week: number;
  year: number;
}

interface SingleUserPlanArgs extends SinglePlanArgs {
  userId: string;
}

export const useUserPlan = ({
  projectId,
  week,
  year,
  userId,
}: SingleUserPlanArgs) => {
  const handleError = useHandlePlanningError();
  const query = getQueryString(week, year);

  // TODO: PERF: this swr call is taking 13-18 ms per call
  const { data, error, ...rest } = useSWR<UserForPlanning[]>(
    `${baseUrl}/project/${projectId}/users/${userId}/?${query}`,
    { revalidateOnFocus: false, revalidateOnMount: false }
  );

  React.useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);

  return {
    userPlan: data?.[0],
    error,
    ...rest,
  };
};

export const useGetAllResources = (revalidateOnMount = false) => {
  const { data, ...rest } = useApi<PlannerResource[]>(
    `${machineBaseUrl}/get-current-company-machines`,
    {
      revalidateOnFocus: false,
      revalidateOnMount,
    }
  );

  return {
    resources: data,
    ...rest,
  };
};

interface SingleMachinePlanArgs extends SinglePlanArgs {
  machineId: string;
}

export const useMachinePlan = ({
  projectId,
  week,
  year,
  machineId,
}: SingleMachinePlanArgs) => {
  const handleError = useHandlePlanningError();
  const query = getQueryString(week, year);

  const { data, error, ...rest } = useSWR<MachineForPlanning[]>(
    `${machineBaseUrl}/project/${projectId}/machines/${machineId}/?${query}`,
    { revalidateOnFocus: false, revalidateOnMount: false }
  );

  React.useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);

  return {
    machinePlan: data?.[0],
    error,
    ...rest,
  };
};

export const useSortUsersForPlanning = () => {
  const handleError = useHandlePlanningError();

  return async (
    projectId: string,
    sortedUserIds: string[],
    menteesSortType: UserSortType
  ): Promise<string[] | null> => {
    try {
      const response = checkStatus(
        await put(
          `${baseUrl}/project/${projectId}/sort-users?menteesSortType=${menteesSortType}`,
          sortedUserIds
        )
      );
      return response.json();
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};

export interface InternalNoteParams {
  fromDate: string;
  toDate: string;
  projectId: string;
  userIds?: string[];
  machineIds?: string[];
}

const getInternalNotesParams = (
  week: number,
  year: number,
  projectId: string
): InternalNoteParams => {
  const { firstDayOfWeek, lastDayOfWeek } = getFirstAndLastDayOfWeek(
    week,
    year
  );

  return {
    fromDate: format(firstDayOfWeek, plannerDateFormat),
    toDate: format(lastDayOfWeek, plannerDateFormat),
    projectId,
  };
};

export const useGetInternalNotes = (revalidateOnMount = false) => {
  const projectId = useAtomValue(projectAtom);
  const { week, year } = useSchedulerParams();

  const commentsRequestParams = React.useMemo<InternalNoteParams>(
    () => getInternalNotesParams(week, year, projectId),
    [week, year, projectId]
  );

  return usePostSWRFetcher<InternalNote[]>(
    projectId ? `${internalCommentBaseUrl}/get-comments` : '',
    commentsRequestParams,
    { revalidateOnMount }
  );
};

export const usePostInternalNote = (monthlyPlanning = false) => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();

  const projectId = useAtomValue(projectAtom);
  const { month } = useMonthlyPlanningParams();

  return async (note: NewInternalNote) => {
    const newNote = { ...note, date: getPlannerDateString(note.date) };
    const week = getWeekForPlanning(note.date);
    const year = note.date.getFullYear();
    const { monthDays } = getMonthlyPlanningDates(year, month);

    // New note is missing some fields from complete notes but it's temporary so we can ignore this error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    mutate<InternalNote[]>(
      [
        `${internalCommentBaseUrl}/get-comments`,
        // Mutating month or week notes with optimistic data
        monthlyPlanning
          ? {
              projectId,
              fromDate: format(monthDays[0], plannerDateFormat),
              toDate: format(monthDays.at(-1)!, plannerDateFormat),
            }
          : getInternalNotesParams(week, year, projectId),
      ],
      async (data) => {
        try {
          const response = checkStatus(
            await post(`${internalCommentBaseUrl}/new-comment`, {
              ...newNote,
              date: getPlannerDateString(newNote.date),
            })
          );
          const createdNote: InternalNote = await response.json();
          showPlanningToast(t('ResourcePlanning.NoteCreated'));

          mutate<InternalNote[]>(
            [
              `${internalCommentBaseUrl}/get-comments`,
              // Mutating the "opposite" notes after note was sucessfully created
              monthlyPlanning
                ? getInternalNotesParams(week, year, projectId)
                : {
                    projectId,
                    fromDate: format(monthDays[0], plannerDateFormat),
                    toDate: format(monthDays.at(-1)!, plannerDateFormat),
                  },
            ],
            (dta) => (dta ? [...dta, createdNote] : [createdNote])
          );

          return data ? [...data, createdNote] : [createdNote];
        } catch (e) {
          handleError(e);
          return data;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) =>
          data
            ? [...data, { ...newNote, id: 'new' }]
            : [{ ...newNote, id: 'new' }],
      }
    );
  };
};

export const usePutInternalNote = (monthlyPlanning = false) => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();

  const projectId = useAtomValue(projectAtom);
  const { month } = useMonthlyPlanningParams();

  return async (note: InternalNote) => {
    const date = parseISO(note.date);
    const newNote = { ...note, date: note.date };
    const week = getWeekForPlanning(date);
    const year = date.getFullYear();
    const { monthDays } = getMonthlyPlanningDates(year, month);

    try {
      mutate<InternalNote[] | undefined>(
        [
          `${internalCommentBaseUrl}/get-comments`,
          // Mutating month or week notes with optimistic data
          monthlyPlanning
            ? {
                projectId,
                fromDate: format(monthDays[0], plannerDateFormat),
                toDate: format(monthDays.at(-1)!, plannerDateFormat),
              }
            : getInternalNotesParams(week, year, projectId),
        ],
        async (data) => {
          const response = checkStatus(
            await put(
              `${internalCommentBaseUrl}/edit-comment/${newNote.id}`,
              newNote
            )
          );
          const createdNote: InternalNote = await response.json();
          showPlanningToast(t('ResourcePlanning.NoteUpdated'));

          mutate<InternalNote[]>(
            [
              `${internalCommentBaseUrl}/get-comments`,
              // Mutating the "opposite" notes after note was sucessfully created
              monthlyPlanning
                ? getInternalNotesParams(week, year, projectId)
                : {
                    projectId,
                    fromDate: format(monthDays[0], plannerDateFormat),
                    toDate: format(monthDays.at(-1)!, plannerDateFormat),
                  },
            ],
            (dta) => (dta ? [...dta, createdNote] : [createdNote])
          );

          return (
            data &&
            data.map((nte) => (nte.id === createdNote.id ? createdNote : nte))
          );
        },
        {
          revalidate: false,
          optimisticData: (data) =>
            data &&
            data.map((nte) =>
              nte.id === newNote.id ? { ...nte, comment: newNote.comment } : nte
            ),
        }
      );
    } catch (e) {
      handleError(e);
    }
  };
};

export const useDeleteInternalNote = (monthlyPlanning = false) => {
  const { t } = useTranslation();
  const handleError = useHandlePlanningError();

  const projectId = useAtomValue(projectAtom);
  const { month } = useMonthlyPlanningParams();

  return async (note: InternalNote) => {
    const date = parseISO(note.date);
    const week = getWeekForPlanning(date);
    const year = date.getFullYear();
    const { monthDays } = getMonthlyPlanningDates(year, month);

    try {
      mutate<InternalNote[] | undefined>(
        [
          `${internalCommentBaseUrl}/get-comments`,
          // Mutating month or week notes with optimistic data
          monthlyPlanning
            ? {
                projectId,
                fromDate: format(monthDays[0], plannerDateFormat),
                toDate: format(monthDays.at(-1)!, plannerDateFormat),
              }
            : getInternalNotesParams(week, year, projectId),
        ],
        async (data) => {
          checkStatus(
            await del(`${internalCommentBaseUrl}/delete-comment/${note.id}`)
          );
          showPlanningToast(t('ResourcePlanning.NoteDeleted'));

          mutate<InternalNote[]>(
            [
              `${internalCommentBaseUrl}/get-comments`,
              // Mutating the "opposite" notes after note was sucessfully created
              monthlyPlanning
                ? getInternalNotesParams(week, year, projectId)
                : {
                    projectId,
                    fromDate: format(monthDays[0], plannerDateFormat),
                    toDate: format(monthDays.at(-1)!, plannerDateFormat),
                  },
            ],
            (dta) => dta && dta.filter((nte) => nte.id !== note.id)
          );

          return data && data.filter((nte) => nte.id !== note.id);
        },
        {
          revalidate: false,
          optimisticData: (data) =>
            data && data.filter((nte) => nte.id !== note.id),
        }
      );
    } catch (e) {
      handleError(e);
    }
  };
};

export const useGetPlanningFilters = (revalidateOnMount = true) => {
  const { week, year } = useSchedulerParams();
  const projectId = useAtomValue(projectAtom);

  const query = getQueryString(week, year);

  return useApi<FiltersForPlanning>(
    `${baseUrl}/get-filters?projectId=${projectId}&${query}`,
    { revalidateOnMount }
  );
};

export const usePlanningTaskTree = (
  projId: string | null,
  revalidateOnMount = false
) => {
  const { data, ...rest } = useApi<ProjectTask[]>(
    projId ? `/api/v3/project/tasks/${projId}/tree` : null,
    {
      revalidateOnMount,
    }
  );

  return {
    data,
    ...rest,
  };
};

export const useGetCompactUserPlans = (
  projectId: string,
  userId: string | null,
  fromDate: Date | null,
  toDate: Date | null,
  skip?: boolean
) =>
  useApi<CompactUserPlans>(
    skip || !userId || !fromDate || !toDate || isSameDay(fromDate, toDate)
      ? null
      : `${baseUrl}/project/${projectId}/user/${userId}/?fromDate=${format(fromDate, plannerDateFormat)}&toDate=${format(toDate, plannerDateFormat)}`
  );

export const useMachinePlansForPeriod = (
  projectId: string,
  machineId: string | null,
  fromDate: Date | null,
  toDate: Date | null,
  revalidateOnMount = false
) =>
  useApi<MachineForPlanning[]>(
    machineId && fromDate && toDate
      ? `${machineBaseUrl}/project/${projectId}/machines/${machineId}/?fromDate=${format(fromDate, plannerDateFormat)}&toDate=${format(toDate, plannerDateFormat)}`
      : null,
    { revalidateOnFocus: false, revalidateOnMount }
  );

interface GatUpdateRequestResponse {
  message: string;
  success: true;
}

export const useFetchGat = () => {
  const handleError = useHandlePlanningError();

  return async (
    projectNumber: string,
    fromDate: string,
    toDate: string
  ): Promise<GatUpdateRequestResponse | null> => {
    const params = new URLSearchParams({ projectNumber, fromDate, toDate });

    try {
      const response = checkStatus(
        await get(`api/shift-plan/fetch-from-server?${params}`)
      );
      return response.json();
    } catch (e) {
      handleError(e);
      return null;
    }
  };
};
